import { logger, LogCategory } from '../utils/logger';

/**
 * EnvironmentService - Manages API environment configuration
 */

export interface Environment {
  name: string;
  label: string;
  apiUrl: string;
  authEndpoint: string;
  websocketUrl?: string;
}

const LOCAL: Environment = {
  name: 'local',
  label: 'Local Development',
  apiUrl: 'http://localhost:8000/api/v1',
  authEndpoint: 'http://localhost:8000/api/v1/auth',
  websocketUrl: 'ws://localhost:8000/ws'
};

const QA: Environment = {
  name: 'qa',
  label: 'QA',
  apiUrl: 'https://api-qa.latio.app/api/v1',
  authEndpoint: 'https://api-qa.latio.app/api/v1/auth',
  websocketUrl: 'wss://api-qa.latio.app/ws'
};

const PRODUCTION: Environment = {
  name: 'production',
  label: 'Production',
  apiUrl: 'https://api.latio.app/api/v1',
  authEndpoint: 'https://api.latio.app/api/v1/auth',
  websocketUrl: 'wss://api.latio.app/ws'
};

const PRODUCTION_HOST = 'admin.latio.app';

/**
 * The same build serves every host, so the host picks the API (INFRA-009):
 * admin.latio.app talks only to production; anywhere else (admin-qa,
 * localhost) offers local + QA and never production.
 */
export function environmentsForHost(hostname: string): Environment[] {
  return hostname === PRODUCTION_HOST ? [PRODUCTION] : [LOCAL, QA];
}

export function defaultEnvironment(environments: Environment[]): Environment {
  return environments.find(env => env.name === 'qa') ?? environments[0];
}

export const ENVIRONMENTS: Environment[] = environmentsForHost(
  typeof window !== 'undefined' ? window.location.hostname : ''
);

export class EnvironmentService {
  private static readonly STORAGE_KEY = 'latio_admin_environment';
  private static currentEnvironment: Environment | null = null;

  /**
   * Get the currently selected environment
   */
  static getCurrentEnvironment(): Environment {
    if (!this.currentEnvironment) {
      this.currentEnvironment = this.loadEnvironment();
    }
    return this.currentEnvironment;
  }

  /**
   * Set the current environment and persist to localStorage
   */
  static setEnvironment(environmentName: string): void {
    const environment = ENVIRONMENTS.find(env => env.name === environmentName);
    if (!environment) {
      throw new Error(`Environment '${environmentName}' not found`);
    }

    this.currentEnvironment = environment;
    localStorage.setItem(this.STORAGE_KEY, environmentName);

    logger.info(LogCategory.UI, `Switched to ${environment.label} (${environment.apiUrl})`, undefined, 'EnvironmentService');
  }

  /**
   * Get all available environments
   */
  static getAvailableEnvironments(): Environment[] {
    return ENVIRONMENTS;
  }

  /**
   * Get the API URL for the current environment
   */
  static getApiUrl(): string {
    return this.getCurrentEnvironment().apiUrl;
  }

  /**
   * Get the auth endpoint for the current environment
   */
  static getAuthEndpoint(): string {
    return this.getCurrentEnvironment().authEndpoint;
  }

  /**
   * Get the WebSocket URL for the current environment
   */
  static getWebSocketUrl(): string | undefined {
    return this.getCurrentEnvironment().websocketUrl;
  }

  /**
   * Load environment from localStorage or return default
   */
  private static loadEnvironment(): Environment {
    const isHttpsPage = typeof window !== 'undefined' && window.location.protocol === 'https:';
    const defaultEnv = defaultEnvironment(ENVIRONMENTS);

    const rawSaved = localStorage.getItem(this.STORAGE_KEY);
    // `development` was renamed to `qa`; existing browsers still have it stored.
    const saved = rawSaved === 'development' ? 'qa' : rawSaved;
    if (saved) {
      const environment = ENVIRONMENTS.find(env => env.name === saved);
      if (environment) {
        // Refuse insecure (http) environments when served over https — browsers block mixed content.
        if (isHttpsPage && environment.apiUrl.startsWith('http://')) {
          logger.info(LogCategory.UI, `Ignoring saved ${environment.label} on HTTPS page; using ${defaultEnv.label}`, undefined, 'EnvironmentService');
          return defaultEnv;
        }
        logger.info(LogCategory.UI, `Loaded ${environment.label} from localStorage`, undefined, 'EnvironmentService');
        return environment;
      }
    }

    logger.info(LogCategory.UI, `Using default environment ${defaultEnv.label}`, undefined, 'EnvironmentService');
    return defaultEnv;
  }

  /**
   * Reset to default environment
   */
  static resetToDefault(): void {
    localStorage.removeItem(this.STORAGE_KEY);
    this.currentEnvironment = null;
    logger.info(LogCategory.UI, 'Reset to default environment', undefined, 'EnvironmentService');
  }
}

export default EnvironmentService;